import { useMemo } from 'react';
import { calculateAgeCategory, calculateAirwayMeasurements } from '../utils/ageUtils';
import { calculateETTSizes, calculateInsertionDepths } from '../utils/ettUtils';
import { parseAgeInput, convertToYears } from '../utils/unitUtils';
import { useCalculationCache, createCacheKey } from '../utils/memoUtils';
import airwayData from '../data/pedsairways.json';

interface AirwayDevice {
  Size: string;
  'ID (mm)': string;
  'OD (mm)': string;
  'Neo Length (mm)'?: string;
  'Ped Length (mm)'?: string;
  'Long Length (mm)'?: string;
  'Flextend Length (mm)'?: string;
}

type AgeCategory = 
  | 'Preterm'
  | 'Under 1 mo'
  | '1-6 mo'
  | '6-18 mo'
  | '1.5 - 3 years'
  | '3 - 6 years'
  | '6 - 9  years'
  | '9-12 years'
  | '12 - 14 years'
  | 'Adult'
  | '';

interface AirwayData {
  [manufacturer: string]: {
    [ageCategory in AgeCategory]: Partial<AirwayDevice>;
  };
}

export const useAirwayCalculations = (
  ageValue: string,
  height: number | null,
  weight: number | null,
  hasSyndrome: boolean = false
) => {
  const cache = useCalculationCache<any>(300000); // 5 minutes TTL

  return useMemo(() => {
    if (!ageValue) return null;

    const cacheKey = createCacheKey(ageValue, height, weight, hasSyndrome);
    const cachedResult = cache.get(cacheKey);
    if (cachedResult) return cachedResult;

    try {
      const { value, unit } = parseAgeInput(ageValue);
      const ageInYears = convertToYears(value, unit);
      const ageCategory = calculateAgeCategory(ageInYears, height ?? 0, weight ?? 0, hasSyndrome);
      const measurements = calculateAirwayMeasurements(ageCategory);

      // Calculate ETT sizes and insertion depths
      const { idealCalculations, adjustedCalculations, bmi, bmiCategory } = calculateETTSizes(ageInYears, height, weight);
      const insertionDepths = calculateInsertionDepths(height);

      // Get recommended devices
      const recommendedDevices: Array<{ manufacturer: string; device: Partial<AirwayDevice> }> = [];
      Object.entries(airwayData as unknown as AirwayData).forEach(([manufacturer, ageCategories]) => {
        const device = ageCategories[ageCategory as AgeCategory];
        if (device && Object.keys(device).length > 0) {
          recommendedDevices.push({ manufacturer, device });
        }
      });

      const result = {
        ageCategory,
        recommendedDevices,
        measurements,
        ettCalculations: {
          idealCalculations,
          adjustedCalculations,
          insertionDepths,
          bmi,
          bmiCategory
        }
      };

      cache.set(cacheKey, result);
      return result;
    } catch (error) {
      throw error;
    }
  }, [ageValue, height, weight, hasSyndrome, cache]);
};